import { BulbIcon, HexagonIcon, QuestionIcon } from './icons';
import { useStrings } from '../state/AppContext';

export interface FooterActionsProps {
  hintsLeft: number;
  insightsLeft: number;
  /** True once the game is over — hints can no longer help. */
  hintDisabled: boolean;
  /**
   * Count of insights already revealed (and so free to re-read) for the
   * current game. Keeps the button enabled at 0 quota so a paid-for
   * definition stays reachable (I2) — the sheet only *reveals* new words
   * when quota allows; it always shows what's already been paid for.
   */
  revealedInsightCount: number;
  onHint: () => void;
  onInsight: () => void;
  onLogo: () => void;
}

function Badge({ count }: { count: number }) {
  return (
    <span
      aria-hidden="true"
      className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-accent text-[11px] font-bold text-white"
    >
      {count}
    </span>
  );
}

export function FooterActions({
  hintsLeft, insightsLeft, hintDisabled, revealedInsightCount, onHint, onInsight, onLogo,
}: FooterActionsProps) {
  const t = useStrings();
  return (
    <div className="flex items-center justify-between gap-2 pb-0 sm:gap-3 sm:pb-safe">
      <button
        type="button"
        aria-label={t.hint.left(hintsLeft)}
        disabled={hintDisabled || hintsLeft === 0}
        onClick={onHint}
        className="glass relative flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold tracking-widest disabled:opacity-50 sm:min-h-[52px]"
      >
        <QuestionIcon className="h-5 w-5 text-dim" />
        {t.hint.label}
        <Badge count={hintsLeft} />
      </button>

      <button
        type="button"
        aria-label={t.common.openMenu}
        onClick={onLogo}
        className="relative grid h-11 w-11 shrink-0 place-items-center rounded-full bg-glass sm:h-14 sm:w-14"
      >
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-grad-accent opacity-25 blur-md"
        />
        <HexagonIcon className="relative h-6 w-6 text-accent sm:h-8 sm:w-8" />
      </button>

      <button
        type="button"
        aria-label={t.insight.left(insightsLeft)}
        disabled={insightsLeft === 0 && revealedInsightCount === 0}
        onClick={onInsight}
        className="glass relative flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold tracking-widest disabled:opacity-50 sm:min-h-[52px]"
      >
        <BulbIcon className="h-5 w-5 text-present" />
        {t.insight.label}
        <Badge count={insightsLeft} />
      </button>
    </div>
  );
}
